/**
 * Grid Helper Functions for Snakes and Ladders
 * Handles coordinate conversions and position calculations
 */

/**
 * Convert square number to grid position (row, col)
 * Snake pattern: bottom-left is 1, snakes right, up, left, up, right...
 *
 * @param {number} squareNumber - 1-indexed square number
 * @param {number} rows - Number of rows
 * @param {number} cols - Number of columns
 * @returns {{row: number, col: number}} - 0-indexed grid position (row 0 is top)
 */
export const squareToGridPosition = (squareNumber, rows, cols) => {
    if (squareNumber < 1 || squareNumber > rows * cols) {
        throw new Error(`Square number ${squareNumber} is out of bounds for ${rows}x${cols} grid`);
    }

    // Convert to 0-indexed
    const index = squareNumber - 1;

    // Calculate row from bottom (0 = bottom row)
    const rowFromBottom = Math.floor(index / cols);

    // Calculate column (alternates direction based on row)
    const posInRow = index % cols;
    const isEvenRow = rowFromBottom % 2 === 0;
    const col = isEvenRow ? posInRow : (cols - 1 - posInRow);

    // Convert row to SVG coordinates (0 = top row)
    const row = rows - 1 - rowFromBottom;

    return { row, col };
};

/**
 * Convert grid position to square number
 *
 * @param {number} row - 0-indexed row (0 is top in SVG)
 * @param {number} col - 0-indexed column (0 is left)
 * @param {number} rows - Number of rows
 * @param {number} cols - Number of columns
 * @returns {number} - 1-indexed square number
 */
export const gridPositionToSquare = (row, col, rows, cols) => {
    // Convert from SVG row (0 = top) to row from bottom
    const rowFromBottom = rows - 1 - row;

    // Determine column position based on row direction
    const isEvenRow = rowFromBottom % 2 === 0;
    const colIndex = isEvenRow ? col : (cols - 1 - col);

    // Calculate square number
    return rowFromBottom * cols + colIndex + 1;
};

/**
 * Get pixel coordinates for center of a square
 *
 * @param {number} squareNumber - 1-indexed square number
 * @param {number} rows - Number of rows
 * @param {number} cols - Number of columns
 * @param {number} cellWidth - Width of each cell in pixels
 * @param {number} cellHeight - Height of each cell in pixels
 * @returns {{x: number, y: number}} - Center coordinates
 */
export const getSquareCenter = (squareNumber, rows, cols, cellWidth, cellHeight) => {
    const { row, col } = squareToGridPosition(squareNumber, rows, cols);
    return {
        x: col * cellWidth + cellWidth / 2,
        y: row * cellHeight + cellHeight / 2
    };
};

/**
 * Get pixel coordinates for a specific position within a square
 *
 * @param {number} squareNumber - 1-indexed square number
 * @param {number} rows - Number of rows
 * @param {number} cols - Number of columns
 * @param {number} cellWidth - Width of each cell in pixels
 * @param {number} cellHeight - Height of each cell in pixels
 * @param {'center'|'top'|'bottom'|'left'|'right'|'top-left'} position - Position within square
 * @returns {{x: number, y: number}} - Coordinates
 */
export const getSquarePosition = (squareNumber, rows, cols, cellWidth, cellHeight, position = 'center') => {
    const { row, col } = squareToGridPosition(squareNumber, rows, cols);
    const baseX = col * cellWidth;
    const baseY = row * cellHeight;

    switch (position) {
        case 'center':
            return { x: baseX + cellWidth / 2, y: baseY + cellHeight / 2 };
        case 'top':
            return { x: baseX + cellWidth / 2, y: baseY };
        case 'bottom':
            return { x: baseX + cellWidth / 2, y: baseY + cellHeight };
        case 'left':
            return { x: baseX, y: baseY + cellHeight / 2 };
        case 'right':
            return { x: baseX + cellWidth, y: baseY + cellHeight / 2 };
        case 'top-left':
            return { x: baseX + 4, y: baseY + 14 }; // Offset for number label
        default:
            return { x: baseX + cellWidth / 2, y: baseY + cellHeight / 2 };
    }
};

/**
 * Generate all square positions for a grid
 *
 * @param {number} rows - Number of rows
 * @param {number} cols - Number of columns
 * @returns {Array<{squareNumber: number, row: number, col: number}>}
 */
export const generateGridSquares = (rows, cols) => {
    const squares = [];
    for (let squareNumber = 1; squareNumber <= rows * cols; squareNumber++) {
        const { row, col } = squareToGridPosition(squareNumber, rows, cols);
        squares.push({ squareNumber, row, col });
    }
    return squares;
};
